import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Card from '../../Card/Card';
import SelectDropDown from '../../FormElements/SelectDropDown/SelectDropDown';
import TabActionButtons from '../../FormElements/TabActionButtons/TabActionButtons';
import styles from './TabSections.module.css';

const PropertiesSettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  const [settings, setSettings] = useState({
    // Responsive View Settings
    hvnly_propertyColumnsGapGrid: '10',
    hvnly_propertyTabletView: '2',
    hvnly_propertySmallTabletView: '2',
    hvnly_propertyMobileTabletView: '1',
    
    // Main Container Width Settings
    hvnly_container_width_xs: '100%',
    hvnly_container_width_sm: '540px',
    hvnly_container_width_md: '720px',
    hvnly_container_width_lg: '960px',
    hvnly_container_width_xl: '1140px',
    hvnly_container_width_xxl: '1320px',
    hvnly_container_width_xxxl: '1399px',
    hvnly_container_width_4k: '1400px',
  });

  useEffect(() => {
    if (settingsData && Object.keys(settingsData).length > 0) {
      setSettings(prev => ({ ...prev, ...settingsData }));
    }
  }, [settingsData]);

  const handleInputChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(settings);
      return true;
    }
    return false;
  };

  const handleReset = async () => {
    const defaultSettings = {
      hvnly_propertyColumnsGapGrid: '10',
      hvnly_propertyTabletView: '2',
      hvnly_propertySmallTabletView: '2',
      hvnly_propertyMobileTabletView: '1',
      hvnly_container_width_xs: '100%',
      hvnly_container_width_sm: '540px',
      hvnly_container_width_md: '720px',
      hvnly_container_width_lg: '960px',
      hvnly_container_width_xl: '1140px',
      hvnly_container_width_xxl: '1320px',
      hvnly_container_width_xxxl: '1399px',
      hvnly_container_width_4k: '1400px',
    };
    setSettings(defaultSettings);
    if (onSettingsChange) {
      onSettingsChange(defaultSettings);
    }
    if (onReset) {
      await onReset();
      return true;
    }
    return false;
  };

  const propertyColumnsGapOptions = [
    { value: '0', label: __('0px', 'havenlytics') },
    { value: '10', label: __('10px', 'havenlytics') },
    { value: '20', label: __('20px', 'havenlytics') },
    { value: '30', label: __('30px', 'havenlytics') },
  ];
  
  const propertyTabletViewOptions = [
    { value: '2', label: '2' }, { value: '3', label: '3' },
    { value: '4', label: '4' }, { value: '5', label: '5' }, { value: '6', label: '6' }
  ];
  
  const propertySmallTabletViewOptions = [
    { value: '2', label: '2' }, { value: '3', label: '3' },
    { value: '4', label: '4' }, { value: '5', label: '5' }, { value: '6', label: '6' }
  ];
  
  const propertySmallMobileViewOptions = [
    { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' },
    { value: '4', label: '4' }, { value: '5', label: '5' }, { value: '6', label: '6' }
  ];

  const containerWidthOptions = [
    { value: '100%', label: __('100% (Full Width)', 'havenlytics') },
    { value: '540px', label: __('540px', 'havenlytics') },
    { value: '720px', label: __('720px', 'havenlytics') },
    { value: '960px', label: __('960px', 'havenlytics') },
    { value: '1140px', label: __('1140px', 'havenlytics') },
    { value: '1320px', label: __('1320px', 'havenlytics') },
    { value: '1399px', label: __('1399px', 'havenlytics') },
    { value: '1400px', label: __('1400px', 'havenlytics') },
    { value: '1600px', label: __('1600px', 'havenlytics') },
    { value: '1800px', label: __('1800px', 'havenlytics') },
  ];

  return (
    <div className={`${styles['hvnly-settings-api__content']} ${styles['hvnly-settings-api__content--active']}`} id="properties">
      <h2>{__('Properties Archive Settings', 'havenlytics')}</h2>

      {/* Main Container Width Card */}
      <Card title={__("Main Container Width", "havenlytics")} icon="fas fa-arrows-alt-h" collapsible>
        <div className={styles['hvnly-settings-api__form-group']}>
          <p className={styles['hvnly-settings-api__description']}>
            {__('Configure the maximum width of the main container at different screen sizes. These values control the responsive behavior of your property pages.', 'havenlytics')}
          </p>
        </div>
        
        <SelectDropDown
          label={__("Extra Small (XS) - Mobile (< 480px)", "havenlytics")}
          id="hvnly_container_width_xs"
          value={settings.hvnly_container_width_xs}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_xs', value)}
        />
        
        <SelectDropDown
          label={__("Small (SM) - Mobile Landscape (480px - 639px)", "havenlytics")}
          id="hvnly_container_width_sm"
          value={settings.hvnly_container_width_sm}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_sm', value)}
        />
        
        <SelectDropDown
          label={__("Medium (MD) - Tablet (640px - 767px)", "havenlytics")}
          id="hvnly_container_width_md"
          value={settings.hvnly_container_width_md}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_md', value)}
        />
        
        <SelectDropDown
          label={__("Large (LG) - Tablet Landscape (768px - 991px)", "havenlytics")}
          id="hvnly_container_width_lg"
          value={settings.hvnly_container_width_lg}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_lg', value)}
        />
        
        <SelectDropDown
          label={__("Extra Large (XL) - Laptop (992px - 1199px)", "havenlytics")}
          id="hvnly_container_width_xl"
          value={settings.hvnly_container_width_xl}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_xl', value)}
        />
        
        <SelectDropDown
          label={__("2X Large (XXL) - Desktop (1200px - 1439px)", "havenlytics")}
          id="hvnly_container_width_xxl"
          value={settings.hvnly_container_width_xxl}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_xxl', value)}
        />
        
        <SelectDropDown
          label={__("3X Large (XXXL) - Large Desktop (1440px - 1919px)", "havenlytics")}
          id="hvnly_container_width_xxxl"
          value={settings.hvnly_container_width_xxxl}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_xxxl', value)}
        />
        
        <SelectDropDown
          label={__("4K / Ultra HD (2560px and above)", "havenlytics")}
          id="hvnly_container_width_4k"
          value={settings.hvnly_container_width_4k}
          options={containerWidthOptions}
          onChange={(value) => handleInputChange('hvnly_container_width_4k', value)}
        />
      </Card>

      {/* Property Responsive View Card */}
      {/* <Card title={__("Property Responsive View", "havenlytics")} icon="fas fa-mobile" collapsible>
        <SelectDropDown
          label={__("Property Columns Gap", "havenlytics")}
          id="hvnly_propertyColumnsGapGrid"
          value={settings.hvnly_propertyColumnsGapGrid}
          options={propertyColumnsGapOptions}
          onChange={(value) => handleInputChange('hvnly_propertyColumnsGapGrid', value)}
        />
        
        <SelectDropDown
          label={__("Property Tablet => Browser Width < 992", "havenlytics")}
          id="hvnly_propertyTabletView"
          value={settings.hvnly_propertyTabletView}
          options={propertyTabletViewOptions}
          onChange={(value) => handleInputChange('hvnly_propertyTabletView', value)}
        />
        
        <SelectDropDown
          label={__("Property Small Tablet => Browser Width < 768", "havenlytics")}
          id="hvnly_propertySmallTabletView"
          value={settings.hvnly_propertySmallTabletView}
          options={propertySmallTabletViewOptions}
          onChange={(value) => handleInputChange('hvnly_propertySmallTabletView', value)}
        />
        
        <SelectDropDown
          label={__("Property Mobile => Browser Width < 480", "havenlytics")}
          id="hvnly_propertyMobileTabletView"
          value={settings.hvnly_propertyMobileTabletView}
          options={propertySmallMobileViewOptions}
          onChange={(value) => handleInputChange('hvnly_propertyMobileTabletView', value)}
        />
      </Card> */}

      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save Properties Settings", "havenlytics")}
        resetLabel={__("Reset Changes", "havenlytics")}
      />
    </div>
  );
};

export default PropertiesSettings;